// Plan ladder: Free scan → Basic → Complete → Ultimate. Prices are mock (€ / month, billed yearly).
import type { Entitlements, Plan, ItemType, Fix } from './types';

const ALL: ItemType[] = ['email', 'phone', 'card', 'national_id', 'bank_account', 'username', 'name', 'address'];
const limits = (n: Partial<Record<ItemType, number>>) => Object.fromEntries(ALL.map((t) => [t, n[t] ?? 0])) as Record<ItemType, number>;

export const PLANS: Record<Plan, { name: string; price: number | null; tagline: string; entitlements: Entitlements }> = {
  free_scan: {
    name: 'Free scan', price: 0, tagline: 'See your exposure once',
    entitlements: { plan: 'free_scan', itemLimits: limits({ email: 1, phone: 1, name: 1 }), sources: ['dark_web', 'osint'],
      osint: { mode: 'preview_once' }, fixModes: [], detailsVisible: false, monitoring: false, partners: [] },
  },
  basic: {
    name: 'Basic', price: 3.99, tagline: 'Dark web monitoring for your emails',
    entitlements: { plan: 'basic', itemLimits: limits({ email: 5 }), sources: ['dark_web'],
      osint: { mode: 'none' }, fixModes: ['guided'], detailsVisible: true, monitoring: true, partners: [] },
  },
  complete: {
    name: 'Complete', price: 6.99, tagline: 'Everything Nord can watch',
    entitlements: { plan: 'complete', itemLimits: limits({ email: 8, phone: 2, card: 4, national_id: 2, bank_account: 3, username: 5, name: 1, address: 2 }),
      sources: ['dark_web', 'transactions', 'osint'], osint: { mode: 'monthly' }, fixModes: ['guided', 'done_for_you'],
      detailsVisible: true, monitoring: true, partners: [] },
  },
  ultimate: {
    name: 'Ultimate', price: 10.99, tagline: 'Monitoring plus removal and insurance',
    entitlements: { plan: 'ultimate', itemLimits: limits({ email: 8, phone: 2, card: 4, national_id: 2, bank_account: 3, username: 5, name: 1, address: 2 }),
      sources: ['dark_web', 'transactions', 'osint', 'incogni', 'coveron'], osint: { mode: 'monthly' }, fixModes: ['guided', 'done_for_you'],
      detailsVisible: true, monitoring: true, partners: ['incogni', 'coveron'] },
  },
};

export const LADDER: Plan[] = ['free_scan', 'basic', 'complete', 'ultimate'];
export const atLeast = (have: Plan, need: Plan) => LADDER.indexOf(have) >= LADDER.indexOf(need);

// Lowest plan that unlocks a capability. Used to label locked UI ("Do it for me · in Complete").
export const canUseFix = (plan: Plan, fix: Fix) => atLeast(plan, fix.minPlan);
export const nextPlanFor = (plan: Plan): Plan | null => LADDER[LADDER.indexOf(plan) + 1] ?? null;

// Coverage: watched types / types the plan allows. Shown as a meter; never part of the score.
export function coverage(plan: Plan, watchedTypes: ItemType[]) {
  const allowed = ALL.filter((t) => (PLANS[plan].entitlements.itemLimits[t] ?? 0) > 0);
  return { watched: new Set(watchedTypes).size, possible: ALL.length, allowedOnPlan: allowed.length };
}

// Preview: any plan can add a type it does not cover, PREVIEW_LIMIT items per type. The item is OTP-verified and
// scanned like any other; its findings arrive locked (metadata only) until a plan that covers the type.
export const PREVIEW_LIMIT = 1;
export const coversType = (plan: Plan, type: ItemType) => (PLANS[plan].entitlements.itemLimits[type] ?? 0) > 0;
export const isPreviewType = (plan: Plan, type: ItemType) => !coversType(plan, type);
// How many items of a type the user may add: the plan's limit, or the preview cap for uncovered types.
export const addLimit = (plan: Plan, type: ItemType) => (coversType(plan, type) ? PLANS[plan].entitlements.itemLimits[type] : PREVIEW_LIMIT);
// Lowest paid plan that covers a type in full.
export const planForType = (type: ItemType): Plan => LADDER.find((p) => p !== 'free_scan' && coversType(p, type)) ?? 'complete';

export interface FindingLock { entryPoint: string; unlockPlan: Plan; type?: ItemType }
// Why a finding is locked, or null when its details are visible.
//   Free scan → every finding (locked_finding). Otherwise → only when every watched item behind it is a preview
//   type (locked_item_<type>); a finding that also touches a covered item stays open.
export function findingLock(plan: Plan, itemTypes: ItemType[]): FindingLock | null {
  if (!PLANS[plan].entitlements.detailsVisible) return { entryPoint: 'locked_finding', unlockPlan: 'complete' };
  if (itemTypes.length === 0 || itemTypes.some((t) => coversType(plan, t))) return null;
  const type = itemTypes[0];
  return { entryPoint: `locked_item_${type}`, unlockPlan: planForType(type), type };
}
